import { translate } from '@rhino-project/core/i18n';
import type { RhinoRead } from '@rhino-project/core';

type ReportValue = RhinoRead<'report'>['report_values'][number];

type RatingValue = {
  id: ReportValue['id'];
  value: ReportValue['value'];
  report_field?: Partial<ReportValue['report_field']> | null;
};

export interface ReportGrade {
  id: unknown;
  name: string;
  value: number;
}

export interface ReportSummary {
  /** Mean of the rating-field values, or null when the report has none. */
  value: number | null;
  /** "Average" for multi-field reports, else the single field's name. */
  label: string;
  /** Rating fields in display order. */
  grades: ReportGrade[];
  /** Whether to show the per-field breakdown (more than one rating field). */
  showGrades: boolean;
}

const fieldOf = (rv: RatingValue) => rv.report_field;

const fieldName = (rv: RatingValue) => {
  const f = fieldOf(rv);
  return f?.display_name ?? f?.name ?? '';
};

export const summarizeReport = (report: {
  report_values?: RatingValue[] | null;
}): ReportSummary => {
  const values = report.report_values ?? [];

  const grades: ReportGrade[] = values
    .filter(
      (rv) =>
        fieldOf(rv)?.field_type === 'rating' &&
        rv.value != null &&
        rv.value !== ''
    )
    .sort((a, b) => (fieldOf(a)?.order ?? 0) - (fieldOf(b)?.order ?? 0))
    .map((rv) => ({ id: rv.id, name: fieldName(rv), value: Number(rv.value) }))
    .filter((g) => !Number.isNaN(g.value));

  const value =
    grades.length > 0
      ? grades.reduce((sum, g) => sum + g.value, 0) / grades.length
      : null;

  const label =
    grades.length === 1
      ? grades[0].name
      : grades.length > 1
        ? translate('frontend.messages.average')
        : '';

  return { value, label, grades, showGrades: grades.length > 1 };
};
